import React, { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { pricingPackages, packageComparison, pricingHighlights } from '../data/pricingData';
import ScrollToTop from './common/ScrollToTop';

const Pricing = () => {
  // Memoize data for performance
  const memoizedPackages = useMemo(() => pricingPackages, []);
  const memoizedComparison = useMemo(() => packageComparison, []);
  const memoizedHighlights = useMemo(() => pricingHighlights, []);

  // Scroll progress indicator effect with animations
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('pricing-scroll-progress');
      if (scrollProgress) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.scrollY;
        const progress = (scrolled / documentHeight) * 100;
        scrollProgress.style.width = `${Math.min(progress, 100)}%`;
        scrollProgress.setAttribute('aria-valuenow', Math.min(progress, 100).toFixed(0));
      }

      // Intersection Observer for animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.style.opacity = '1';
          }
        });
      }, observerOptions);

      // Observe elements
      const animatedElements = document.querySelectorAll('.animate-on-scroll');
      animatedElements.forEach((el) => observer.observe(el));

      return () => {
        animatedElements.forEach((el) => observer.unobserve(el));
      };
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const packages = [
    {
      name: 'DIY',
      subtitle: 'Self Navigator',
      price: '400,000',
      refundable: 'Non-refundable',
      description: 'For confident self-starters who want expert verification',
      features: [
        'Document verification for up to 5 countries',
        'SOP/CV/LoR format standards + 1 feedback round',
        'Portal access & status tracking',
        'Eligibility assessment',
        'Profile setup',
        'Document checklists'
      ],
      cta: 'Choose DIY',
      link: '/wizard',
      popular: false
    },
    {
      name: 'Plus',
      subtitle: 'Guided Success',
      price: '850,000',
      refundable: 'Partially refundable',
      description: 'End-to-end handling for one country with premium essentials',
      features: [
        'Everything in DIY',
        'Full university + visa management for 1 country',
        'SOP/LoR editing & application management',
        'Visa documentation & appointment support',
        'Public health insurance setup (DE)',
        'Blocked account setup & guidance (DE)',
        'Study Loan Assistance (Canada): Up to 75% tuition',
        'Referral commission LKR 25,000 per referral',
        'Structured payment (60% / 40%)'
      ],
      highlight: 'Germany health insurance & blocked account included',
      cta: 'Choose Plus',
      link: '/wizard',
      popular: false
    },
    {
      name: 'PRO',
      subtitle: 'All-Inclusive Elite',
      price: '1,190,000',
      refundable: 'Partially refundable',
      description: 'Maximum safety, global reach, and VIP support across all 5 countries',
      features: [
        'Everything in Plus',
        'Multi-country coverage (all 5 included)',
        'Minimum 7 university applications (5 public + 2 private)',
        'Application Success Guarantee: Free re-application (worth LKR 850,000)',
        'Dedicated consultant with priority support',
        'Free IELTS preparation course (worth LKR 80,000)',
        'Travel insurance (first month) included',
        'Student & alumni network access',
        'Referral commission LKR 50,000 per referral',
        '25% Family Visa Discount — spouse/dependents',
        '100GB cloud storage with dedicated email',
        'Google Gemini Pro for 1 year with 2TB storage',
        'Structured payment (40% / 30% / 30%)'
      ],
      highlight: 'Most Popular - Best Value',
      cta: 'Go PRO',
      link: '/wizard',
      popular: true
    }
  ];

  const comparisons = [
    {
      feature: 'Countries Covered',
      diy: 'Up to 5',
      plus: '1 Country',
      pro: 'All 5 Countries'
    },
    {
      feature: 'University Applications',
      diy: 'DIY Support',
      plus: 'Managed',
      pro: 'Min. 7 Applications'
    },
    {
      feature: 'Document Verification',
      diy: '✓',
      plus: '✓',
      pro: '✓'
    },
    {
      feature: 'SOP/CV/LoR Support',
      diy: 'Format Standards',
      plus: 'Full Editing',
      pro: 'Full Editing + Priority'
    },
    {
      feature: 'Visa Support',
      diy: 'Guidelines',
      plus: 'Full Support',
      pro: 'Full Support + Priority'
    },
    {
      feature: 'Germany Health Insurance',
      diy: '—',
      plus: '✓',
      pro: '✓'
    },
    {
      feature: 'Blocked Account Setup',
      diy: '—',
      plus: '✓',
      pro: '✓'
    },
    {
      feature: 'Canada Study Loan',
      diy: '—',
      plus: '✓',
      pro: '✓'
    },
    {
      feature: 'IELTS Preparation',
      diy: '—',
      plus: '—',
      pro: 'Free (worth LKR 80K)'
    },
    {
      feature: 'Dedicated Consultant',
      diy: '—',
      plus: '—',
      pro: '✓'
    },
    {
      feature: 'Application Success Guarantee',
      diy: '—',
      plus: '—',
      pro: '✓ (worth LKR 850K)'
    },
    {
      feature: 'Family Visa Discount',
      diy: '—',
      plus: '—',
      pro: '25% Off'
    },
    {
      feature: 'Cloud Storage',
      diy: '—',
      plus: '—',
      pro: '100GB + Dedicated Email'
    },
    {
      feature: 'Google Gemini Pro',
      diy: '—',
      plus: '—',
      pro: '1 Year + 2TB'
    },
    {
      feature: 'Referral Commission',
      diy: '—',
      plus: 'LKR 25,000',
      pro: 'LKR 50,000'
    }
  ];

  const faqs = [
    {
      q: 'What is the Application Success Guarantee?',
      a: 'With the PRO package, if all your university applications are rejected, we provide a free re-application service for the next semester (valued at LKR 850,000). This ensures you have maximum support in securing admission.'
    },
    {
      q: 'How does the Canada study loan work?',
      a: 'We connect you with our lending partners who can provide up to 75% of your tuition fees. The loan is typically repayable over 4–7 years at ~11% APR, subject to lender eligibility and terms.'
    },
    {
      q: 'Can I upgrade from DIY to Plus or PRO?',
      a: 'Yes! You can upgrade your package at any time. The price difference will be adjusted, and you\'ll gain access to all additional features of the upgraded package.'
    },
    {
      q: 'What does "partially refundable" mean?',
      a: 'Plus and PRO packages include partial refunds as outlined in your signed service agreement. The refund terms depend on the stage of the application process and are clearly defined in your contract.'
    },
    {
      q: 'Is the IELTS course really free with PRO?',
      a: 'Yes! The PRO package includes a comprehensive IELTS preparation course worth LKR 80,000 at no additional cost. This includes study materials, practice tests, and expert guidance.'
    },
    {
      q: 'How does the family visa discount work?',
      a: 'PRO members receive a 25% discount on visa services for spouses and dependents. This applies to family reunion visas and dependent visa applications across all supported countries.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pricing - 3 Transparent Packages | Ravana Migration</title>
        <meta name="description" content="Choose from DIY (LKR 400K), Plus (LKR 850K), or PRO (LKR 1.19M) packages. Transparent pricing with no hidden fees. Contract-first approach with partial refunds available." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 py-16 sm:py-20 lg:py-24">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center animate-fade-in-up" style={{opacity: 0}}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="relative inline-block">
                  <span className="relative z-10">Simple, Transparent</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-6 text-purple-400 opacity-30" viewBox="0 0 200 20" fill="currentColor">
                    <path d="M0 15 Q 50 5 100 15 T 200 15 L 200 20 L 0 20 Z" />
                  </svg>
                </span>{' '}
                Packages
              </h1>
              <div className="rule mx-auto mb-6"></div>
              <p className="text-xl sm:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
                All packages include eligibility assessment, profile setup, document checklists, and client portal access
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">Contract-First Approach</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">No Hidden Fees</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">Bank Transfer Only</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid md:grid-cols-3 gap-8 animate-on-scroll" style={{opacity: 0}}>
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`package-card hover-lift animate-on-scroll ${pkg.popular ? 'pro pt-8' : 'pt-2'} ${pkg.popular ? 'border-2 border-yellow-400 relative' : 'border-2 border-gray-200 relative'}`}
                style={{animationDelay: `${index * 0.1}s`, opacity: 0}}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 text-sm font-bold px-6 py-2 rounded-full shadow-xl flex items-center gap-1 whitespace-nowrap">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Flight Plan™ {pkg.name}</h2>
                  <p className="text-sm text-gray-600 mb-4 font-medium">{pkg.subtitle}</p>

                  <div className="mb-4">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-lg text-gray-600">LKR</span>
                      <span className="text-4xl sm:text-5xl font-bold text-gray-900">{pkg.price}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 inline-block">{pkg.refundable}</p>
                  </div>

                  <p className="text-sm text-gray-700 mb-6">{pkg.description}</p>
                </div>

                {pkg.highlight && (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-xl p-4 mb-6 text-center shadow-md">
                    <p className="text-sm font-semibold text-gray-900">{pkg.highlight}</p>
                  </div>
                )}

                <div className="space-y-2 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-3">
                      <svg className={`w-5 h-5 ${pkg.popular ? 'text-blue-600' : 'text-green-600'} flex-shrink-0 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={pkg.link}
                  className={`btn ${pkg.popular ? 'btn-accent' : 'btn-outline'} w-full hover-lift group flex items-center justify-center gap-2`}
                >
                  <span>{pkg.cta}</span>
                  <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-gray-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Compare Packages
              </h2>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-lg sm:text-xl text-gray-600">
                See what's included in each package
              </p>
            </div>

            <div className="overflow-x-auto animate-on-scroll" style={{opacity: 0}}>
              <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-purple-600">
                    <th className="px-6 py-5 text-left text-sm font-bold text-white">Feature</th>
                    <th className="px-6 py-5 text-center text-sm font-bold text-white">DIY</th>
                    <th className="px-6 py-5 text-center text-sm font-bold text-white">Plus</th>
                    <th className="px-6 py-5 text-center text-sm font-bold text-yellow-300">PRO</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map((row, index) => (
                    <tr key={index} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.feature}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 text-center">{row.diy}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 text-center">{row.plus}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold text-center">{row.pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-12 animate-on-scroll" style={{opacity: 0}}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">
              Got questions about our packages?
            </p>
          </div>

          <div className="space-y-6 animate-on-scroll" style={{opacity: 0}}>
            {faqs.map((faq, index) => (
              <div key={index} className="card card-hover group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{faq.q}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl animate-on-scroll" style={{opacity: 0}}>
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700"></div>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float" style={{animationDelay: '2s'}}></div>
            </div>

            <div className="relative text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Ready to Begin Your Global Journey?
              </h2>
              <div className="rule mx-auto mb-6 bg-white/30"></div>
              <p className="text-lg sm:text-xl text-white mb-10 max-w-3xl mx-auto leading-relaxed">
                Join hundreds of students now studying in Germany, Canada, Australia, the UK, and New Zealand
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                <Link to="/choice" className="btn bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group">
                  <span>Start Your Free Assessment</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link to="/choice" className="btn btn-outline text-white border-white/80 hover:bg-white hover:text-blue-600 text-lg px-8 py-4 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
                  <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>Speak to a Consultant</span>
                </Link>
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-white px-4">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">No sign-up required</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">6 minutes</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Instant results</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
